import React, { useMemo } from 'react'
import { DcChart, DcDiv, DcFlexBox, DcText, colors } from 'pixel-base'
import { InventoryProduct } from './types'
import { InventoryStats } from './useInventoryStats'

interface StockTurnoverChartProps {
  data: InventoryProduct[]
  loading: boolean
  error: string | null
  onProductClick?: (product: InventoryProduct) => void
  showGaugeOnly?: boolean
  showScatterPlotOnly?: boolean
  stats?: InventoryStats
  onTitleClick?: () => void
}

const StockTurnoverChart: React.FC<StockTurnoverChartProps> = ({
  data,
  loading,
  error,
  onProductClick,
  showGaugeOnly = false,
  showScatterPlotOnly = false,
  stats,
  onTitleClick,
}) => {
  // Use stats data for turnover or calculate from data as fallback
  const turnoverStats = useMemo(() => {
    // Use stats data if available
    if (stats?.turnover_performance) {
      return {
        average: stats.turnover_performance.metrics.avg_turnover_ratio,
        max: stats.turnover_performance.metrics.avg_turnover_ratio * 2, // Estimate max
        min: 0,
        score: stats.turnover_performance.score,
        status: stats.turnover_performance.status,
      }
    }

    // Fallback to calculating from data
    if (!data || data.length === 0) {
      return { average: 0, max: 0, min: 0, score: 0, status: 'unknown' }
    }

    const turnovers = data.map((p) => p.turnover_ratio || 0)
    const average = turnovers.reduce((sum, t) => sum + t, 0) / turnovers.length
    const max = Math.max(...turnovers)
    const min = Math.min(...turnovers)

    return { average, max, min, score: average * 20, status: 'good' }
  }, [data, stats])

  // Turnover gauge data
  const gaugeData = useMemo(() => {
    // Use score from stats if available, otherwise use average turnover
    const value = turnoverStats.score || turnoverStats.average
    let color = colors.info
    let status = turnoverStats.status || 'Good'

    // Determine color based on status or score
    if (status === 'critical' || value < 30) {
      color = colors.danger
      status = 'Critical'
    } else if (status === 'warning' || value < 60) {
      color = colors.warning
      status = 'Warning'
    } else if (status === 'good' || value < 80) {
      color = colors.primary
      status = 'Good'
    } else {
      color = colors.success
      status = 'Excellent'
    }

    return {
      series: [
        {
          name: 'Turnover Performance',
          type: 'gauge',
          min: 0,
          max: 100,
          splitNumber: 5,
          radius: '90%',
          axisLine: {
            lineStyle: {
              width: 10,
              color: [
                [0.2, colors.danger],
                [0.4, colors.warning],
                [0.6, colors.info],
                [0.8, colors.success],
                [1, colors.success],
              ],
            },
          },
          pointer: {
            itemStyle: {
              color: color,
            },
          },
          axisTick: {
            distance: -10,
            length: 10,
            lineStyle: {
              color: '#fff',
              width: 2,
            },
          },
          splitLine: {
            distance: -10,
            length: 10,
            lineStyle: {
              color: '#fff',
              width: 4,
            },
          },
          axisLabel: {
            color: '#fff',
            distance: -25,
            fontSize: 12,
          },
          detail: {
            valueAnimation: true,
            formatter: '{value}%',
            color: color,
            fontSize: 24,
            fontWeight: 'bold',
          },
          data: [
            {
              value: value,
              name: 'Turnover Performance',
            },
          ],
        },
      ],
      status,
      color,
    }
  }, [turnoverStats])

  // Transform data for scatter plot (inventory value vs turnover ratio)
  const scatterData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        series: [
          {
            name: 'Value vs Turnover',
            type: 'scatter',
            data: [],
            symbolSize: (data: number[]) => Math.sqrt(data[2]) * 2,
          },
        ],
      }
    }

    const scatterPoints = data.map((product) => [
      product.inventory_value || 0,
      product.turnover_ratio || 0,
      product.days_to_turnover || 0, // Used for bubble size
      product.product_name,
    ])

    return {
      series: [
        {
          name: 'Value vs Turnover',
          type: 'scatter',
          data: scatterPoints,
          symbolSize: (data: number[]) =>
            Math.max(5, Math.min(30, Math.sqrt(data[2]) * 2)),
          itemStyle: {
            color: (params: any) => {
              const daysToTurnover = params.data[2]
              if (daysToTurnover > 365) return colors.danger
              if (daysToTurnover > 180) return colors.warning
              if (daysToTurnover > 90) return colors.info
              return colors.success
            },
            opacity: 0.7,
          },
        },
      ],
    }
  }, [data])

  const gaugeOptions = useMemo(
    () => ({
      tooltip: {
        formatter: (params: any) => `
          <div style="padding: 8px;">
            <strong>Turnover Performance</strong><br/>
            Average: ${params.value.toFixed(2)}<br/>
            Status: ${gaugeData.status}<br/>
            Max: ${turnoverStats.max.toFixed(2)}<br/>
            Min: ${turnoverStats.min.toFixed(2)}
          </div>
        `,
      },
      series: gaugeData.series,
    }),
    [gaugeData, turnoverStats],
  )

  const scatterOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => `
          <div style="padding: 8px;">
            <strong>${params.data[3]}</strong><br/>
            Inventory Value: $${params.data[0].toLocaleString()}<br/>
            Turnover Ratio: ${params.data[1].toFixed(2)}<br/>
            Days to Turnover: ${params.data[2]}
          </div>
        `,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Inventory Value (₨)',
        axisLabel: {
          formatter: (value: number) => `₨${(value / 1000).toFixed(0)}k`,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Turnover Ratio',
        axisLabel: {
          formatter: '{value}',
        },
      },
      series: scatterData.series,
    }),
    [scatterData],
  )

  if (loading) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText>Loading inventory data...</DcText>
      </DcDiv>
    )
  }

  if (error) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText color={colors.danger}>Error loading data: {error}</DcText>
      </DcDiv>
    )
  }

  // Show only gauge
  if (showGaugeOnly) {
    return (
      <DcDiv>
        <DcDiv style={{ position: 'relative' }}>
          <DcChart
            type="gauge"
            data={gaugeData}
            options={gaugeOptions}
            height={300}
            width="100%"
            title="Turnover Performance"
            isLoading={loading}
            isTitleClickable={!!onTitleClick}
            onClickTitle={onTitleClick}
          />
          {/* Status indicator */}
          <DcDiv
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
            }}
          >
            <DcText
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: gaugeData.color,
              }}
            >
              {gaugeData.status}
            </DcText>
          </DcDiv>
        </DcDiv>

        {/* Additional Metrics */}
        {stats?.turnover_performance && (
          <DcDiv
            style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: colors.background,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
            }}
          >
            <DcText
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: colors.text,
                marginBottom: '12px',
              }}
            >
              Turnover Performance Metrics
            </DcText>

            <DcDiv
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
                fontSize: '12px',
              }}
            >
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Avg Turnover Ratio
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.text }}>
                  {stats.turnover_performance.metrics.avg_turnover_ratio.toFixed(
                    1,
                  )}
                  x
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Excellent Turnover
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.success }}>
                  {
                    stats.turnover_performance.metrics
                      .excellent_turnover_products
                  }
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Good Turnover
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.primary }}>
                  {stats.turnover_performance.metrics.good_turnover_products}
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>Dead Stock</DcText>
                <DcText style={{ fontWeight: '600', color: colors.danger }}>
                  {stats.turnover_performance.metrics.dead_stock_products}
                </DcText>
              </DcDiv>
            </DcDiv>
          </DcDiv>
        )}
      </DcDiv>
    )
  }

  // Show only scatter plot
  if (showScatterPlotOnly) {
    return (
      <DcDiv>
        <DcChart
          type="scatter"
          data={scatterData}
          options={scatterOptions}
          height={300}
          width="100%"
          isLoading={loading}
        />
      </DcDiv>
    )
  }

  // Show both (original layout)
  return (
    <DcDiv>
      <DcFlexBox direction="column" gap="20px" height="auto">
        {/* Primary Chart - Turnover Gauge */}
        <DcDiv>
          <DcDiv
            style={{
              position: 'relative',
              maxWidth: '400px',
              margin: '0 auto',
            }}
          >
            <DcChart
              type="gauge"
              data={gaugeData}
              options={gaugeOptions}
              height={300}
              width="100%"
              title="Turnover Performance"
              isLoading={loading}
              isTitleClickable={!!onTitleClick}
              onClickTitle={onTitleClick}
            />
            {/* Status indicator */}
            <DcDiv
              style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                textAlign: 'center',
              }}
            >
              <DcText
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: gaugeData.color,
                }}
              >
                {gaugeData.status}
              </DcText>
            </DcDiv>
          </DcDiv>
        </DcDiv>

        {/* Secondary Chart - Scatter Plot */}
        <DcDiv>
          <DcText
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: colors.textPrimary,
            }}
          >
            Inventory Value vs Turnover Ratio
          </DcText>
          <DcText
            style={{
              fontSize: '12px',
              color: colors.textSecondary,
              marginBottom: '12px',
            }}
          >
            Bubble size represents days to turnover
          </DcText>
          <DcChart
            type="scatter"
            data={scatterData}
            options={scatterOptions}
            height={300}
            width="100%"
            isLoading={loading}
            onClick={(params: any) => {
              if (onProductClick && data[params.dataIndex]) {
                onProductClick(data[params.dataIndex])
              }
            }}
          />
        </DcDiv>
      </DcFlexBox>
    </DcDiv>
  )
}

export default StockTurnoverChart
